/**
 * Puzzle data model. Mirrors puzzles/schema.json — change both together.
 * Coordinates are decimal degrees, 4 decimals max.
 */

export type Level = "easy" | "medium" | "hard";
export type PuzzleSet = "adult" | "family";
export type Precision = "exact" | "approximate" | "fictional";
/** Every day is Pin it, Next stop, Whose steps?, in that order (DECISIONS 2026-09-30). */
export type RoundType = "pin" | "next" | "who";
export const ROUND_ORDER = ["pin", "next", "who"] as const;

/**
 * Theme tags (DECISIONS 2026-09-29 "Theme tags"): 1–2 per round, from this fixed list only.
 * Not facts: no fact IDs, no fact check; editable on published days. `war` never in the family set.
 * Labels per level in src/strings/en.ts (`themes`). Adding one needs a DECISIONS.md line.
 */
export const THEMES = ["history", "journeys", "sport", "screen", "books", "music", "science", "art", "war", "leaders", "myths", "places", "nature"] as const;
export type Theme = (typeof THEMES)[number];

/** Continents for person attributes (`from`): where a person was born, or a thing, character or event comes from. */
export const CONTINENTS = ["Africa", "Asia", "Europe", "North America", "South America", "Oceania", "Antarctica"] as const;
export type Continent = (typeof CONTINENTS)[number];

/**
 * What the "Whose steps?" clue line compares after a wrong guess (DECISIONS 2026-10-03). Any may be null when it
 * doesn't apply (shown as "—"). `century`: of the person's main activity, 16 = the 1500s, -5 = the 5th century BC.
 * `field`: their main theme tag. `from`: continent of birth (people) or origin (things, characters, events).
 */
export interface NameAttrs {
  century: number | null;
  field: Theme | null;
  from: Continent | null;
}

/** puzzles/name-attrs.json: one entry per name in names.json. Attributes are facts: sourced and checked like rounds. */
export interface NameAttrsEntry extends NameAttrs {
  sources: string[];
  verified?: Verified;
  flags?: string[];
}

/** [[northLat, westLng], [southLat, eastLng]] — the box the map opens on in Easy. */
export type Region = [[number, number], [number, number]];

export interface Place {
  name: string;
  lat: number;
  lng: number;
  when?: string;
  note?: string;
  precision?: Precision;
  /** Wikidata item for the place (e.g. "Q597" = Lisbon), so spellings like "Mumbai (Bombay)" count as one place on the Knowledge Map. */
  wikidata?: string;
}

export interface Verified {
  by: string;
  /** ISO date, YYYY-MM-DD */
  date: string;
  /**
   * human (default): a person checked every fact. agents: two independent agent checks agreed
   * (scripts/checks.ts); counts once its batch is approved in puzzles/check-batches.json (automatic since 2026-09-30).
   */
  method?: "human" | "agents";
  /** Check batch id; required when method is "agents". */
  batch?: string;
}

export interface Base {
  /** e.g. "adult-003-2" = adult set, day 3, round 2 */
  id: string;
  type: RoundType;
  title: string;
  /** 1–3 sentences shown after the round. Optional where per-item notes explain instead. */
  explain?: string;
  sources: string[];
  /** Absent until every fact and coordinate has been checked (two independent agent checks). */
  verified?: Verified;
  /** Open questions. A round with flags cannot be scheduled. */
  flags?: string[];
  /** 1–2 theme tags (required on scheduled days; see THEMES). */
  themes?: Theme[];
}

export interface PinRound extends Base {
  type: "pin";
  /** One moment from someone's story, without the place. */
  clue: string;
  /** Where it happened: one pinnable point. With a `trail`, it needs `when` (with a year) and a `note`. */
  answer: Place;
  scaleKm: number;
  /**
   * Up to two hints, revealed one at a time before Lock in; each lowers the most the round can score (HINT_CAPS).
   * Hint 1 narrows to roughly a country (~500 km), hint 2 to roughly a city (~100 km); ≤ 110 characters; never the
   * answer's name. Days from #29 need two. A Family day not yet migrated uses `easy.hint` as hint 1.
   */
  hints?: [string] | [string, string];
  /** Family: the box the map opens on. `hint` is the pre-2026-10-03 free hint, read as hint 1 when there are no `hints`. */
  easy?: { region: Region; hint?: string };
  /**
   * Two more of the person's steps, drawn after the round with the answer as a 3-point route in time order
   * (DECISIONS 2026-10-03): normally one before and one after the moment; two after if the moment is their
   * first notable step, two before if it is their last. Each needs `when` (with a year) and a `note`.
   * Knowledge Map context facts `t0`, `t1`.
   */
  trail?: [Place, Place];
}

export interface WhoRound extends Base {
  type: "who";
  /** 5 places, hardest clue first, giveaway last. */
  clues: Place[];
  answer: string;
  aliases: string[];
  /** The answer is a thing (a ship, a band), not a person — changes the intro line. */
  something?: boolean;
  /** 3 same-kind decoys, used for the pick-from-four in Easy. */
  decoys?: string[];
  /** Reveal card after the round: the full route (defaults to the clues) and one surprising line. */
  reveal?: { line: string; route?: Place[] };
}

export interface NextRound extends Base {
  type: "next";
  prompt: string;
  /** The journey so far, in order; drawn on the map during the round. */
  route: Place[];
  /** The documented next stop. The player drops a pin, scored on the distance table like Pin it. */
  answer: Place;
  /** Frames the map around the answer after the round (km). */
  scaleKm?: number;
  /** Family level: a hint that narrows down where to look. */
  easy?: { hint: string };
}

export type Round = PinRound | NextRound | WhoRound;

export interface Day {
  /** e.g. "adult-003" */
  id: string;
  set: PuzzleSet;
  number: number;
  /** Publication date (YYYY-MM-DD). Absent = draft, not scheduled. */
  date?: string;
  /** Shown after finishing the previous day: a non-spoiling hint about this day. Checked with the day. */
  teaser?: string;
  /** Always Pin it, Next stop, Whose steps? (DECISIONS 2026-09-30). */
  rounds: [PinRound, NextRound, WhoRound];
}

/** A scheduled day as the production bundle knows it: no content, just where to fetch it. */
export interface DayRef {
  set: PuzzleSet;
  number: number;
  date: string;
}
